import {Box, Dialog, DialogContent, Grid, IconButton, MenuItem, Select, styled} from "@mui/material";
import NavigateBreadcrumbs from "../components/navBreadcrumbs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchBox from "../components/searchBox";
import {historicalAlertSearchFields} from "../util/searchFields";
import {DataGrid, GridActionsCellItem, GridRowModes} from "@mui/x-data-grid";
import CustomToolbar from "../components/gridToolbar";
import {generateImageName, PAGE_SIZE, updateData} from "../util/util";
import React, {useContext, useEffect, useLayoutEffect, useState} from "react";
import Cookies from "js-cookie";
import {BASE_URL, fileUrl, routes, useGetDataList, useSearch} from "../routes/api";
import {EnumContext} from "../components/enumContext";
import {HistoricalAlertListHeaders} from "../util/getFields";
import {alertFields} from "../util/modelFields";
import {useLocation, useNavigate} from "react-router-dom";
import {LoadingComponent, NoDataComponent} from "../util/loading";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import ErrorDisplay from "../components/errorDisplay";

export default function HistoricalAlertList() {
    const token = Cookies.get('token');
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions')).unit :
        {create: false, edit: false, delete: false, view: false};
    const webAdmin = Cookies.get('webAdmin') === 'T';
    const route = routes.alert;
    const {enums} = useContext(EnumContext);

    const [displayToAlias, setDisplayToAlias] = useState({});
    const [images, setImages] = React.useState(() => new Map());
    const [rowModesModel, setRowModesModel] = useState({});
    const [imageOpen, setImageOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);

    const WrapCell = styled('div')({
        whiteSpace: 'normal', // allow wrapping
        wordWrap: 'break-word',
        lineHeight: '1.5',
        paddingTop: '10px',
        paddingBottom: '10px',
    });

    const viewHeaders = [...HistoricalAlertListHeaders.filter(key => !['id'].includes(key)).map((key) =>
        ({
            field: key,
            headerName: alertFields.fields[key].display,
            type: key === 'al_status' ? 'singleSelect' : 'string',
            minWidth: 100,
            flex: 1,
            editable: ['al_notes', 'al_status'].includes(key),
            hide: key === 'id',
            valueOptions: key === 'al_status' ? ['New', 'Dismissed'] : [],
            cellClassName: (params) => {
                const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
                return isInEditMode ?
                    (['al_notes', 'al_status'].includes(key) ?
                        (key === 'al_notes' ? 'editable-cell' : '') :
                        'nonEditable-cell') :
                    '';
            },
            valueGetter: (value) => (Array.isArray(value) ?
                    value.reduce((acc, item) => (acc ? `${acc}, ${item}` : item), '') :
                    value
            ),
            renderCell: (params) => {
                return <WrapCell>{params.value}</WrapCell>
            },
        })
    ),
        {
            field: 'image',
            type: 'actions',
            headerName: 'Image',
            width: 150,
            cellClassName: 'actions',
            getActions: (params) => {
                if (params.row.related_image_uris && params.row.related_image_uris.length > 0) {
                    return [<GridActionsCellItem
                        icon={<ImageIcon/>}
                        label="Image"
                        // className="textPrimary"
                        onClick={handleViewImageClick(params)}
                        color="inherit"
                    />];
                }
                return [];
            }
        },
        {
            field: 'edit',
            type: 'actions',
            headerName: 'Edit Notes',
            width: 100,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon/>}
                            label="Save"
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon/>}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />];
                }
                return [<GridActionsCellItem
                    icon={<EditIcon/>}
                    label="Edit"
                    className="textPrimary"
                    onClick={handleEditClick(id)}
                    color="inherit"
                />];
            }
        }
    ];

    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from ? location.state.from : {pathname: `/`};

    const [alerts, setAlerts] = useState([]);
    const [filteredAlerts, setFilteredAlerts] = useState([]);
    const [searchParams, setSearchParams] = useState({});
    const [searching, setSearching] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [noData, setNoData] = useState(false);
    const [totalPages, setTotalPages] = useState(null);

    const [alert, setAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusCode, setStatusCode] = useState(0);

    const {
        data: alertData,
        loading: alertLoading,
        statusCode: alertStatus,
        handleCall: alertCall
    } = useGetDataList(token, route);

    const {
        data: searchData,
        loading: searchLoading,
        statusCode: searchStatus,
        handleCall: searchCall
    } = useSearch(token, route);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        al_timeDismissed: false,
        al_status: false,
        al_dismisser: false,
        al_tag: false,
        related_unit: false,
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: PAGE_SIZE,
    });

    useLayoutEffect(() => {
        if (location?.state?.from?.pathname?.split('/')[1] === 'historical-alert' || !location?.state?.from?.pathname) {
            setPaginationModel({...paginationModel, page: Number(sessionStorage.getItem('alertPage'))});
            setSearchParams(JSON?.parse(sessionStorage.getItem('alertSearchParams')) || {});
            setSearching(sessionStorage.getItem('alertSearching') === 'T');
            setOpenSearch(sessionStorage.getItem('alertOpenSearch') === 'T');
            setColumnVisibilityModel(JSON?.parse(sessionStorage.getItem('alertColumns')) || {
                al_timeDismissed: false,
                al_dismisser: false,
                al_status: false,
                al_tag: false,
                related_unit: false,
            });
        } else {
            setPaginationModel({...paginationModel, page: 0});
            sessionStorage.setItem('alertPage', '0');
            sessionStorage.setItem('alertSearchParams', '{}');
            sessionStorage.setItem('alertSearching', 'F');
            sessionStorage.setItem('alertOpenSearch', 'F');
            sessionStorage.setItem('alertColumns', JSON.stringify({
                al_timeDismissed: false,
                al_dismisser: false,
                al_status: false,
                al_tag: false,
                related_unit: false,
            }));
        }
    }, []);


    const fetchData = async () => {
        try {
            if (searching) {
                await searchCall({params: {...searchParams, page: paginationModel.page + 1, al_status: 'DSM'}});
            } else {
                await alertCall({params: {page: paginationModel.page + 1, al_status: 'DSM'}});
            }
        } catch (error) {
            console.error('Error fetching alert data:', error);
        }
    }

    useEffect(() => {
        setDisplayToAlias(Object.entries(enums.al_status?.data ?? []).reduce((acc, [key, value]) => ({
            ...acc,
            [value]: key
        }), {}));
    }, [enums]);

    useEffect(() => {
        paginationModel.page >= 0 && fetchData();
    }, [paginationModel]);

    useEffect(() => {
        if (alertStatus === 204 || alertData?.count === 0 || alertData?.results?.length === 0) {
            setNoData(true);
        } else if (alertData?.results?.length > 0) {
            setAlerts(alertData.results.map(data => updateData(data, null, 'al', enums)));
            setNoData(false);
            setTotalPages(Math.ceil(alertData.count / PAGE_SIZE));
        } else {
            setNoData(false);
        }
    }, [alertData, alertStatus]);


    useEffect(() => {
        if (searchStatus === 204 || searchData?.count === 0 || searchData?.results?.length === 0) {
        } else if (searchData.results?.length > 0) {
            setFilteredAlerts(searchData.results.map(data => updateData(data, null, 'al', enums)));
            setTotalPages(Math.ceil(searchData.count / PAGE_SIZE));
        } else {
            setTotalPages(0);
        }
    }, [searchData, searchStatus]);

    const handleDownloadImageClick = async () => {
        const image = images.get(currentImage);
        const img = new Image();
        img.src = image.url;
        const link = document.createElement("a");
        link.href = image.url;
        link.download = image.name;
        link.type = image.type;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleViewImageClick = (params) => async () => {
        setCurrentImage(params.id);

        if (images.has(params.id)) {
            setImageOpen(true);
            return;
        }

        const response = await fetch(fileUrl(params.row.related_image_uris[0]), {
            headers: {
                'Authorization': `Token ${token}`,
            },
        });
        if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const newImages = images.set(params.id, {
                url: objectUrl,
                type: blob.type,
                name: generateImageName(params.row.related_unit, params.row.al_record)
            });
            if (images.size > 5) {
                const oldestKey = newImages.keys().next().value;
                if (images.has(oldestKey)) {
                    URL.revokeObjectURL(images.get(oldestKey.url));
                }
                newImages.delete(oldestKey);
            }
            setImages(newImages);
            setImageOpen(true);
        }
    }

    const handlePopupClose = () => {
        setCurrentImage(null);
        setImageOpen(false);
    }

    const handleEditClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    };


    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });
    };

    const processRowUpdate = (newRow, oldRow) => {
        return new Promise(async (resolve, reject) => {
            if ((newRow.al_notes || '').trim() === (oldRow.al_notes || '').trim() && (newRow.al_status === oldRow.al_status)) {
                resolve(oldRow);
                return oldRow;
            }

            try {
                const resp = await fetch(`${BASE_URL}${route}/${newRow.id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json', 'Authorization': `Token ${token}`,
                    },
                    body: JSON.stringify({
                        al_notes: (newRow.al_notes || '').trim(),
                        al_status: displayToAlias[newRow.al_status]
                    })
                })
                setStatusCode(resp.status);
                if (resp.status >= 200 && resp.status < 300) {
                    resolve(newRow);
                } else if (resp.status > 300) {
                    reject(await resp.json());
                    // Redirect to login page
                    if (resp.status === 401) {
                        Cookies.remove('token');
                        Cookies.remove('permissions');
                        Cookies.remove('username');
                        navigate('/login', {state: {from: location}});
                    }
                }
            } catch (error) {
                setAlert(true);
                setErrorMessage(error);
                reject(error);
            }
        });
    };

    const handleProcessRowUpdateError = (error) => {
        setErrorMessage(error);
    }

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleReset = () => {
        setColumnVisibilityModel({
            al_timeDismissed: false,
            al_dismisser: false,
            al_status: false,
            al_tag: false,
            related_unit: false,
        });
        sessionStorage.setItem('alertColumns', JSON.stringify({
            al_timeDismissed: false,
            al_dismisser: false,
            al_status: false,
            al_tag: false,
            related_unit: false,
        }));
    };

    const handleOpenSearch = () => {
        setOpenSearch(true);
        sessionStorage.setItem('alertOpenSearch', 'T');
    }

    const handleCancelSearch = () => {
        if (searching) {
            sessionStorage.setItem('alertPage', '0');
            setPaginationModel((pm) => ({...pm, page: 0}));
        }
        setOpenSearch(false);
        sessionStorage.setItem('alertOpenSearch', 'F');
        setSearching(false);
        sessionStorage.setItem('alertSearching', 'F');
        setSearchParams({});
        sessionStorage.setItem('alertSearchParams', '{}');
        setFilteredAlerts([]);
    }

    const handleSearchParams = (params) => {
        sessionStorage.setItem('alertSearchParams', JSON.stringify(params));
        setSearchParams(params);
    }

    const handleSearch = async () => {
        setFilteredAlerts([]);
        setSearching(true);
        sessionStorage.setItem('alertSearching', 'T');
        setPaginationModel((pm) => ({...pm, page: 0}));
        sessionStorage.setItem('alertPage', '0');

    }

    const handlePaginationModelUpdate = (model) => {
        setPaginationModel(model);
        sessionStorage.setItem('alertPage', model.page);
    }

    const handlePageSelect = (e) => {
        setPaginationModel((pm) => ({...pm, page: e.target.value}));
        sessionStorage.setItem('alertPage', e.target.value);
    }

    const handleColumnVisibilityLabel = (columns) => {
        sessionStorage.setItem('alertColumns', JSON.stringify(columns));
        setColumnVisibilityModel(columns);
    }

    useLayoutEffect(() => {
        if (!(permissions.view || webAdmin)) {
            navigate(from);
        }
    }, []);

    if ((!searching && alertLoading) || (searching && searchLoading)) return <LoadingComponent/>;

    if (noData) return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justify="center"
        >
            <Grid size={12}/>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='historical-alert' display='Historical Alert'/>
            </Grid>
            <Grid size={12}>
                <Typography variant='h4'>Historical Alert</Typography>
            </Grid>
            <Grid size={12}>
                <NoDataComponent/>
            </Grid>
        </Grid>
    )

    return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justifyContent="center"
        >
            <Grid size={12}>
                <ErrorDisplay
                    fields={alertFields}
                    alert={alert}
                    setAlert={setAlert}
                    response={errorMessage}
                    statusCode={statusCode}
                />
            </Grid>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='historical-alerts' display='Historical Alerts'/>
            </Grid>
            <Grid size={9}>
                <Typography variant='h4'>Historical Alerts</Typography>
            </Grid>
            <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button
                    component="label"
                    onClick={openSearch ? handleCancelSearch : handleOpenSearch}
                    startIcon={<ManageSearchIcon/>}
                    sx={{mb: 1}}>
                    {openSearch ? "Cancel" : "Search"}
                </Button>
            </Grid>
            {openSearch && <Grid size={12}>
                <SearchBox
                    prefix='a'
                    searchParams={searchParams}
                    setSearchParams={handleSearchParams}
                    searchFields={historicalAlertSearchFields}
                    sendSearch={handleSearch}
                    sx={{margin: 'auto', mt: 2, mb: 5, maxWidth: '100%'}}
                />
            </Grid>}
            <Grid size={12}/>
            {searching && <Grid size={12}>
                <Typography variant='h6'>
                    Filtered Results:
                </Typography>
            </Grid>}
            <Grid size={12} sx={{display: 'flex', flexDirection: 'column'}}>
                <DataGrid
                    disableColumnFilter
                    disableDensitySelector
                    columns={viewHeaders}
                    rows={searching ? filteredAlerts : alerts}
                    rowCount={(searching ? searchData.count : alertData.count) || 0}
                    pagination
                    paginationMode="server"
                    slots={{toolbar: CustomToolbar}}
                    showToolbar
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelUpdate}
                    pageSizeOptions={[PAGE_SIZE]}
                    editMode="row"
                    getRowHeight={() => 'auto'}
                    disableSelectionOnClick
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityLabel}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    sx={{
                        '& .nonEditable-cell ': {
                            backgroundColor: '#f0f0f0 !important',
                            color: '#808080 !important'
                        },
                        '& .editable-cell ': {
                            border: '0.75px solid #385480 !important'
                        },
                        '& .MuiDataGrid-cell ': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                    slotProps={{
                        columnsManagement: {
                            disableResetButton: true,
                        },
                        toolbar: {handleReset: handleReset},
                    }}
                />
            </Grid>
            {Boolean(totalPages) &&
                <Grid size={12} sx={{display: "flex", justifyContent: "flex-end", alignItems: 'center'}}>
                    <Typography sx={{mr: 1}}>Page: </Typography>
                    <Select
                        name='Page'
                        key='page'
                        onChange={handlePageSelect}
                        value={paginationModel.page}
                        sx={{
                            minWidth: 70,
                            '& .MuiSelect-select': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        {Array.from({length: totalPages}, (_, index) => (
                            <MenuItem key={index} value={index}>
                                {index + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>}
            <Dialog
                open={imageOpen}
                onClose={handlePopupClose}
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            margin: 3,
                            padding: 0,
                            overflow: 'hidden',
                            display: 'block',
                            borderRadius: 2,
                        }
                    }
                }}
            >
                <IconButton
                    onClick={handleDownloadImageClick}
                    aria-label="Download"
                    size="medium"
                    sx={{position: 'absolute', right: 8, top: 8, zIndex: 1, bgcolor: 'background.paper'}}
                >
                    <DownloadIcon fontSize="medium"/>
                </IconButton>

                <DialogContent sx={{p: 0}}>
                    <Box
                        component="img"
                        src={images.get(currentImage)?.url}
                        alt={images.get(currentImage)?.name}
                        // Image grows up to viewport minus margins; otherwise uses its natural size.
                        sx={{
                            padding: 2,
                            display: 'block',
                            width: 'auto',
                            height: 'auto',
                            maxWidth: (t) => `calc(100vw - 48px - ${t.spacing(4)})`,
                            maxHeight: (t) => `calc(100vh - 48px - ${t.spacing(4)})`,
                            objectFit: 'contain',
                            userSelect: 'none',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    )
}